import {useMemo} from 'react'
import {Empty, Flex, Space, Table, Tag, Typography} from 'antd'
import {DeleteOutlined, DownloadOutlined, EditOutlined} from '@ant-design/icons'
import EllipsisTooltip from '../../common/EllipsisTooltip'
import RecordsCount from '../../common/RecordsCount'
import TableActions from '../../common/TableActions'
import {useIntl} from 'react-intl'
import {formatEmptyValue} from '../../../utils/common'

const AppsTable = ({
  dataSource,
  pagination = false,
  getAppsListData = null,
  onEdit = null,
  onDelete = null,
  onDownloadCredentials = null,
}) => {
  const intl = useIntl()

  const columns = useMemo(
    () => [
      {
        title: intl.formatMessage({ id: 'apps.name' }),
        dataIndex: 'name',
        width: '30%',
        sorter: (a, b) => (a.name || '').localeCompare(b.name || ''),
        render: (text) => (
          <EllipsisTooltip className="ellipsis">
            {formatEmptyValue(text)}
          </EllipsisTooltip>
        ),
      },
      {
        title: intl.formatMessage({ id: 'apps.makes' }),
        dataIndex: 'makes',
        width: '25%',
        render: (makes) =>
          makes?.length ? (
            <Flex wrap gap={4} data-testid="app-makes">
              {makes.map((make) => (
                <Tag key={make}>{make}</Tag>
              ))}
            </Flex>
          ) : (
            formatEmptyValue(null)
          ),
      },
      {
        title: intl.formatMessage({ id: 'apps.clientId' }),
        dataIndex: 'client_id',
        width: '35%',
        sorter: (a, b) => (a.client_id || '').localeCompare(b.client_id || ''),
        render: (text) => (
          <EllipsisTooltip className="ellipsis">
            {formatEmptyValue(text)}
          </EllipsisTooltip>
        ),
      },
      {
        title: intl.formatMessage({ id: 'apps.action' }),
        align: 'right',
        width: 135,
        render: (_, record) => (
          <TableActions
            actions={[
              {
                key: 'edit',
                label: intl.formatMessage({ id: 'forms.edit' }),
                icon: <EditOutlined />,
                onClick: () => onEdit(record),
              },
              {
                key: 'credentials',
                label: intl.formatMessage({ id: 'apps.downloadCredentials' }),
                icon: <DownloadOutlined />,
                onClick: () => onDownloadCredentials(record),
              },
              {
                key: 'delete',
                label: intl.formatMessage({ id: 'forms.delete' }),
                icon: <DeleteOutlined />,
                danger: true,
                onClick: () => onDelete(record),
              },
            ]}
          />
        ),
      },
    ],
    [dataSource, pagination]
  )

  return (
    <Space
      direction="vertical"
      size="middle"
      style={{ width: '100%', verticalAlign: 'top' }}
    >
      <Table
        rowKey={(record) => `app-${record.id}`}
        columns={columns}
        dataSource={dataSource}
        showSorterTooltip={false}
        scroll={{
          y: '70vh',
        }}
        pagination={
          pagination
            ? {
                current: pagination?.currentPage,
                total: pagination?.totalCount,
                pageSize: 10,
                showSizeChanger: false,
                hideOnSinglePage: true,
                showTotal: (total) => <RecordsCount count={total} />,
                onChange: (page) => {
                  const offset = (page - 1) * 10
                  const url = pagination?.nextPageLink
                    ? new URL(pagination?.nextPageLink)
                    : new URL(pagination?.prevPageLink)
                  url.searchParams.set('offset', offset)
                  getAppsListData(url.toString())
                },
              }
            : false
        }
        locale={{
          emptyText: (
            <div style={{ padding: '40px' }}>
              <Empty description={<RecordsCount count={0} />} />
            </div>
          ),
        }}
      />
      {pagination && pagination?.totalPages <= 1 && pagination?.totalCount > 0 && (
        <Typography.Text>
          <RecordsCount count={pagination?.totalCount} />
        </Typography.Text>
      )}
    </Space>
  )
}

export default AppsTable
